/**
 * useIncassoShared
 *
 * Shared state hook for both IncassoKanbanPage and IncassoListPage.
 * Manages filters, stats, modal state, and kanban-specific settings.
 *
 * Follows the same pattern as useHelpdeskShared.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSession, useUser } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { toast } from 'react-toastify';
import type { FilterSection, FilterChip } from 'shared/components/filterBar/types';
import {
  IncassoTicket,
  IncassoPhase,
  IncassoBeoordeling,
  IncassoStats,
  IncassoTicketQueryParams,
  IncassoDebtBucket,
} from '../incassoTypes';
import { DEFAULT_HIDDEN_PHASES, incassoPhaseColumns } from '../../configs/incassoPhaseConfig';

// ─── Constants ────────────────────────────────────────────────────────────────

const LS_KEY_HIDDEN_COLUMNS = 'incasso_hidden_columns';
const LS_KEY_FONT_SIZE = 'incasso_font_size';
const FONT_SIZE_MIN = 10;
const FONT_SIZE_MAX = 16;
const FONT_SIZE_DEFAULT = 12;
const FONT_SIZE_STEP = 1;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getFromURL = (key: string): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get(key) ?? '';
};

const getArrayFromURL = (key: string): string[] => {
  const params = new URLSearchParams(window.location.search);
  const val = params.get(key);
  if (!val) return [];
  return val.split(',').map((v) => v.trim()).filter(Boolean);
};

const loadHiddenColumns = (): IncassoPhase[] => {
  try {
    const raw = localStorage.getItem(LS_KEY_HIDDEN_COLUMNS);
    if (raw) return JSON.parse(raw) as IncassoPhase[];
  } catch {
    /* ignore */
  }
  return DEFAULT_HIDDEN_PHASES;
};

const saveHiddenColumns = (cols: IncassoPhase[]) => {
  try {
    localStorage.setItem(LS_KEY_HIDDEN_COLUMNS, JSON.stringify(cols));
  } catch {
    /* ignore */
  }
};

const loadFontSize = (): number => {
  try {
    const raw = localStorage.getItem(LS_KEY_FONT_SIZE);
    if (raw) return Number(raw);
  } catch {
    /* ignore */
  }
  return FONT_SIZE_DEFAULT;
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useIncassoShared = (viewMode: 'kanban' | 'list') => {
  const { session } = useSession();
  const { user } = useUser();

  // Stable ref for session — avoids re-triggering fetches on Clerk token refresh / tab focus
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // ─── Filter state ─────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState(() => getFromURL('search'));
  const [beoordelingFilter, setBeoordelingFilter] = useState<IncassoBeoordeling | undefined>(() => {
    const v = getFromURL('beoordeling') as IncassoBeoordeling;
    return Object.values(IncassoBeoordeling).includes(v) ? v : undefined;
  });
  const [buildingFilters, setBuildingFilters] = useState<string[]>(() => getArrayFromURL('buildingNames'));
  const [companyFilters, setCompanyFilters] = useState<string[]>(() => getArrayFromURL('companyNames'));
  const [phaseFilter, setPhaseFilter] = useState<IncassoPhase | undefined>(() => {
    const v = getFromURL('phase') as IncassoPhase;
    return Object.values(IncassoPhase).includes(v) ? v : undefined;
  });
  const [debtBucket, setDebtBucket] = useState<IncassoDebtBucket | undefined>(() => {
    const v = getFromURL('debtBucket');
    return v === 'BELOW_500' || v === 'FROM_500' ? v : undefined;
  });
  const [includeSnoozed, setIncludeSnoozed] = useState(false);
  const [noIncassoActions, setNoIncassoActions] = useState(() => getFromURL('noIncassoActions') === 'true');

  // ─── Filter options (fetched from backend) ────────────────────────────────
  const [allBuildings, setAllBuildings] = useState<string[]>([]);
  const [allCompanies, setAllCompanies] = useState<string[]>([]);

  // Track whether we're restoring from URL (to suppress redundant setSearchText)
  const [isRestoringFromURL, setIsRestoringFromURL] = useState(false);

  // ─── Stats state ──────────────────────────────────────────────────────────
  const [stats, setStats] = useState<IncassoStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // ─── Key figures (totalUnpaidRent for stats bar) ─────────────────────────
  const [totalUnpaidRent, setTotalUnpaidRent] = useState<number | null>(null);

  // ─── Kanban-specific state ────────────────────────────────────────────────
  const [hiddenKanbanColumns, setHiddenKanbanColumns] = useState<IncassoPhase[]>(loadHiddenColumns);
  const [kanbanFontSize, setKanbanFontSize] = useState<number>(loadFontSize);

  // ─── Modal state ──────────────────────────────────────────────────────────
  // Snooze
  const [snoozeModal, setSnoozeModal] = useState<{
    open: boolean;
    ticket: IncassoTicket | null;
  }>({ open: false, ticket: null });

  // Phase transition
  const [phaseTransitionPending, setPhaseTransitionPending] = useState<{
    ticket: IncassoTicket;
    fromPhase: IncassoPhase;
    toPhase: IncassoPhase;
  } | null>(null);

  // Existing modals (linked by ticket)
  const [actionModal, setActionModal] = useState<IncassoTicket | null>(null);
  const [legalModal, setLegalModal] = useState<IncassoTicket | null>(null);
  const [paymentPlanModal, setPaymentPlanModal] = useState<IncassoTicket | null>(null);

  // ─── Build query filters for API ──────────────────────────────────────────
  const apiFilters = useMemo((): Omit<IncassoTicketQueryParams, 'page' | 'pageSize'> => {
    const f: Omit<IncassoTicketQueryParams, 'page' | 'pageSize'> = {
      includeSnoozed,
    };
    if (searchText) f.search = searchText;
    if (beoordelingFilter) f.beoordeling = beoordelingFilter;
    if (buildingFilters.length > 0) f.buildingNames = buildingFilters;
    if (companyFilters.length > 0) f.companyNames = companyFilters;
    if (phaseFilter) f.phase = phaseFilter;
    if (debtBucket) f.debtBucket = debtBucket;
    if (noIncassoActions) f.noIncassoActions = true;
    return f;
  }, [searchText, beoordelingFilter, buildingFilters, companyFilters, includeSnoozed, phaseFilter, debtBucket, noIncassoActions]);

  // ─── No auto-sync on mount ────────────────────────────────────────────────
  // Table shows snapshot data (last sync + snapshot). Users must click
  // "Vernieuwen" to fetch fresh data from Odoo. Nightly cron also refreshes.
  const [hasSynced] = useState(true);

  // ─── Fetch stats ──────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    if (!sessionRef.current) return;
    setIsLoadingStats(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets/stats`,
        { method: 'GET' },
        sessionRef.current,
      );
      if (!response.ok) throw new Error('Failed to fetch incasso stats');
      const data: IncassoStats = await response.json();
      setStats(data);
    } catch (error) {
      console.error('[useIncassoShared] Error fetching stats:', error);
      // Don't toast — stats are non-critical
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  // ─── Fetch key figures (totalUnpaidRent) ──────────────────────────────────
  const fetchKeyFigures = useCallback(async () => {
    if (!sessionRef.current) return;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/key-figures`,
        { method: 'GET' },
        sessionRef.current,
      );
      if (!response.ok) throw new Error('Failed to fetch key figures');
      const { data } = await response.json();
      setTotalUnpaidRent(data?.currentUnpaidRent ?? null);
    } catch (error) {
      console.error('[useIncassoShared] Error fetching key figures:', error);
    }
  }, []);

  // ─── Fetch filter options ─────────────────────────────────────────────────
  const fetchFilterOptions = useCallback(async () => {
    if (!sessionRef.current) return;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets/filter-options`,
        { method: 'GET' },
        sessionRef.current,
      );
      if (!response.ok) throw new Error('Failed to fetch filter options');
      const data: { buildingNames: string[]; companyNames: string[] } = await response.json();
      setAllBuildings(data.buildingNames);
      setAllCompanies(data.companyNames);
    } catch (error) {
      console.error('[useIncassoShared] Error fetching filter options:', error);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    if (hasSynced) {
      fetchStats();
      fetchKeyFigures();
    }
  }, [hasSynced, fetchStats, fetchKeyFigures]);

  // ─── Update ticket fields (optimistic) ───────────────────────────────────
  const handleUpdateTicket = useCallback(
    (ticketId: string, updates: Partial<IncassoTicket>) => {
      // This is called by child components for optimistic updates.
      // The kanban data hook manages its own local state, so this is primarily
      // a pass-through to re-sync stats if needed.
      console.debug('[useIncassoShared] handleUpdateTicket', ticketId, updates);
    },
    [],
  );

  // ─── Phase change via drag-drop ───────────────────────────────────────────
  const handlePhaseChange = useCallback(
    async (ticketId: string, newPhase: IncassoPhase) => {
      if (!sessionRef.current) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/incasso-tickets/${ticketId}/phase`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phase: newPhase, userId: user?.id }),
          },
          sessionRef.current,
        );

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message ?? 'Fase bijwerken mislukt');
        }

        await fetchStats();
      } catch (error) {
        console.error('[useIncassoShared] Error updating phase:', error);
        throw error; // Re-throw so IncassoKanbanView can rollback optimistic update
      }
    },
    [fetchStats, user?.id],
  );

  // ─── Snooze ───────────────────────────────────────────────────────────────
  const handleSnooze = useCallback(
    async (ticket: IncassoTicket, snoozeUntil: string, reason: string) => {
      if (!sessionRef.current || !user?.id) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/incasso-tickets/${ticket.id}/snooze`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              snoozeUntil,
              snoozeReason: reason,
              userId: user.id,
            }),
          },
          sessionRef.current,
        );

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message ?? 'Snooze instellen mislukt');
        }

        toast.success('Dossier gesnoozed');
        setSnoozeModal({ open: false, ticket: null });
        await fetchStats();
      } catch (error) {
        console.error('[useIncassoShared] Error snoozing ticket:', error);
        toast.error('Fout bij snoozen van dossier');
        throw error;
      }
    },
    [fetchStats, user?.id],
  );

  const handleUnsnooze = useCallback(
    async (ticket: IncassoTicket) => {
      if (!sessionRef.current || !user?.id) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/incasso-tickets/${ticket.id}/snooze`,
          {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: user.id }),
          },
          sessionRef.current,
        );

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message ?? 'Snooze opheffen mislukt');
        }

        toast.success('Snooze opgeheven');
        setSnoozeModal({ open: false, ticket: null });
        await fetchStats();
      } catch (error) {
        console.error('[useIncassoShared] Error unsnoozing ticket:', error);
        toast.error('Fout bij opheffen van snooze');
        throw error;
      }
    },
    [fetchStats, user?.id],
  );

  // ─── Beoordeling change ──────────────────────────────────────────────────
  const handleBeoordelingChange = useCallback(
    async (ticket: IncassoTicket, beoordeling: IncassoBeoordeling | null, beoordelingReden?: string) => {
      if (!sessionRef.current) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/incasso-tickets/${ticket.id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              beoordeling,
              beoordelingReden: beoordelingReden ?? null,
            }),
          },
          sessionRef.current,
        );

        if (!response.ok) throw new Error('Beoordeling bijwerken mislukt');
      } catch (error) {
        console.error('[useIncassoShared] Error updating beoordeling:', error);
        toast.error('Fout bij bijwerken van beoordeling');
        throw error;
      }
    },
    [],
  );

  // ─── Kanban column visibility ─────────────────────────────────────────────
  const handleToggleColumnVisibility = useCallback((columnId: string) => {
    setHiddenKanbanColumns((prev) => {
      const phase = columnId as IncassoPhase;
      const next = prev.includes(phase)
        ? prev.filter((c) => c !== phase)
        : [...prev, phase];
      saveHiddenColumns(next);
      return next;
    });
  }, []);

  // ─── Font size controls ───────────────────────────────────────────────────
  const handleIncreaseFontSize = useCallback(() => {
    setKanbanFontSize((prev) => {
      const next = Math.min(prev + FONT_SIZE_STEP, FONT_SIZE_MAX);
      localStorage.setItem(LS_KEY_FONT_SIZE, String(next));
      return next;
    });
  }, []);

  const handleDecreaseFontSize = useCallback(() => {
    setKanbanFontSize((prev) => {
      const next = Math.max(prev - FONT_SIZE_STEP, FONT_SIZE_MIN);
      localStorage.setItem(LS_KEY_FONT_SIZE, String(next));
      return next;
    });
  }, []);

  // ─── FilterBar sections ───────────────────────────────────────────────────
  const filterBarSections: FilterSection[] = useMemo(
    () => [
      {
        key: 'beoordeling',
        label: 'Beoordeling',
        color: 'moss',
        options: [
          { key: IncassoBeoordeling.KOMT_IN_ORDE, label: 'Komt in orde' },
          { key: IncassoBeoordeling.NIET_BEREIKBAAR, label: 'Kan niet bereiken' },
          { key: IncassoBeoordeling.KOMT_NIET_IN_ORDE, label: 'Komt niet in orde' },
        ],
        selectedValues: beoordelingFilter ? [beoordelingFilter] : [],
        onUpdate: (values) =>
          setBeoordelingFilter(values[0] as IncassoBeoordeling | undefined),
        singleSelect: true,
      },
      {
        key: 'phase',
        label: 'Fase',
        color: 'neutral',
        options: incassoPhaseColumns.map((col) => ({
          key: col.id,
          label: col.label,
        })),
        selectedValues: phaseFilter ? [phaseFilter] : [],
        onUpdate: (values) =>
          setPhaseFilter(values[0] as IncassoPhase | undefined),
        singleSelect: true,
      },
      {
        key: 'debtBucket',
        label: 'Openstaand bedrag',
        color: 'moss',
        options: [
          { key: 'BELOW_500', label: 'Onder € 500' },
          { key: 'FROM_500', label: '€ 500 of meer' },
        ],
        selectedValues: debtBucket ? [debtBucket] : [],
        onUpdate: (values) =>
          setDebtBucket(values[0] as IncassoDebtBucket | undefined),
        singleSelect: true,
      },
      {
        key: 'noIncassoActions',
        label: 'Incasso acties',
        color: 'moss',
        options: [{ key: 'true', label: 'Geen acties ondernomen' }],
        selectedValues: noIncassoActions ? ['true'] : [],
        onUpdate: (values) => setNoIncassoActions(values.includes('true')),
        singleSelect: true,
      },
      {
        key: 'building',
        label: 'Gebouw',
        color: 'neutral',
        options: allBuildings.map((name) => ({ key: name, label: name })),
        selectedValues: buildingFilters,
        onUpdate: (values) => setBuildingFilters(values),
      },
      {
        key: 'company',
        label: 'Vennootschap',
        color: 'neutral',
        options: allCompanies.map((name) => ({ key: name, label: name })),
        selectedValues: companyFilters,
        onUpdate: (values) => setCompanyFilters(values),
      },
    ],
    [beoordelingFilter, phaseFilter, debtBucket, noIncassoActions, allBuildings, buildingFilters, allCompanies, companyFilters],
  );

  // ─── FilterBar chips (active filter indicators) ───────────────────────────
  const filterBarChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (beoordelingFilter) {
      const labels: Record<IncassoBeoordeling, string> = {
        [IncassoBeoordeling.KOMT_IN_ORDE]: 'Komt in orde',
        [IncassoBeoordeling.NIET_BEREIKBAAR]: 'Kan niet bereiken',
        [IncassoBeoordeling.KOMT_NIET_IN_ORDE]: 'Komt niet in orde',
      };
      chips.push({
        key: 'beoordeling',
        label: labels[beoordelingFilter],
        category: 'Beoordeling',
        color: 'moss',
        onRemove: () => setBeoordelingFilter(undefined),
      });
    }

    if (phaseFilter) {
      const col = incassoPhaseColumns.find((c) => c.id === phaseFilter);
      chips.push({
        key: 'phase',
        label: col?.label ?? phaseFilter,
        category: 'Fase',
        color: 'neutral',
        onRemove: () => setPhaseFilter(undefined),
      });
    }

    if (debtBucket) {
      chips.push({
        key: 'debtBucket',
        label: debtBucket === 'BELOW_500' ? 'Onder € 500' : '€ 500 of meer',
        category: 'Openstaand bedrag',
        color: 'moss',
        onRemove: () => setDebtBucket(undefined),
      });
    }

    buildingFilters.forEach((building) => {
      chips.push({
        key: `building-${building}`,
        label: building,
        category: 'Gebouw',
        color: 'neutral',
        onRemove: () => setBuildingFilters((prev) => prev.filter((b) => b !== building)),
      });
    });

    companyFilters.forEach((company) => {
      chips.push({
        key: `company-${company}`,
        label: company,
        category: 'Vennootschap',
        color: 'neutral',
        onRemove: () => setCompanyFilters((prev) => prev.filter((c) => c !== company)),
      });
    });

    if (noIncassoActions) {
      chips.push({
        key: 'noIncassoActions',
        label: 'Geen acties ondernomen',
        category: 'Incasso acties',
        color: 'moss',
        onRemove: () => setNoIncassoActions(false),
      });
    }

    if (includeSnoozed) {
      chips.push({
        key: 'includeSnoozed',
        label: 'Inclusief gesnoozed',
        color: 'yellow',
        onRemove: () => setIncludeSnoozed(false),
      });
    }

    return chips;
  }, [beoordelingFilter, phaseFilter, debtBucket, noIncassoActions, buildingFilters, companyFilters, includeSnoozed]);

  // ─── URL filter sync ──────────────────────────────────────────────────────
  const handleFiltersFromURL = useCallback(
    (urlFilters: { search?: string; filterSections?: Record<string, string[]> }) => {
      setIsRestoringFromURL(true);

      if (urlFilters.search !== undefined) {
        setSearchText(urlFilters.search);
      }
      if (urlFilters.filterSections?.beoordeling?.[0]) {
        setBeoordelingFilter(urlFilters.filterSections.beoordeling[0] as IncassoBeoordeling);
      }
      if (urlFilters.filterSections?.phase?.[0]) {
        setPhaseFilter(urlFilters.filterSections.phase[0] as IncassoPhase);
      }
      const urlDebtBucket = urlFilters.filterSections?.debtBucket?.[0];
      if (urlDebtBucket === 'BELOW_500' || urlDebtBucket === 'FROM_500') {
        setDebtBucket(urlDebtBucket);
      }
      if (urlFilters.filterSections?.building) {
        setBuildingFilters(urlFilters.filterSections.building);
      }
      if (urlFilters.filterSections?.company) {
        setCompanyFilters(urlFilters.filterSections.company);
      }
      if (urlFilters.filterSections?.noIncassoActions?.[0] === 'true') {
        setNoIncassoActions(true);
      }

      requestAnimationFrame(() => setIsRestoringFromURL(false));
    },
    [],
  );

  // ─── Excel export ─────────────────────────────────────────────────────────
  const [isExporting, setIsExporting] = useState(false);

  const handleExportToExcel = useCallback(async () => {
    if (!sessionRef.current || isExporting) return;
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (apiFilters.search) params.set('search', apiFilters.search);
      if (apiFilters.phase) params.set('phase', apiFilters.phase);
      if (apiFilters.beoordeling) params.set('beoordeling', apiFilters.beoordeling);
      if (apiFilters.buildingNames?.length) params.set('buildingNames', apiFilters.buildingNames.join(','));
      if (apiFilters.companyNames?.length) params.set('companyNames', apiFilters.companyNames.join(','));
      if (apiFilters.debtBucket) params.set('debtBucket', apiFilters.debtBucket);
      if (apiFilters.includeSnoozed) params.set('includeSnoozed', 'true');
      if (apiFilters.noIncassoActions) params.set('noIncassoActions', 'true');

      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets/export?${params}`,
        { method: 'GET' },
        sessionRef.current,
      );

      if (!response.ok) throw new Error('Export mislukt');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `incasso-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export succesvol gedownload');
    } catch (error) {
      console.error('[useIncassoShared] Error exporting to Excel:', error);
      toast.error('Fout bij exporteren naar Excel');
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, apiFilters]);

  // ─── Manual refresh (sync + snapshot) ────────────────────────────────────────
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    if (!sessionRef.current || isRefreshing) return;
    setIsRefreshing(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets/sync-and-snapshot`,
        { method: 'POST' },
        sessionRef.current,
      );
      if (!response.ok) throw new Error('Sync + snapshot mislukt');
      toast.success('Incasso data vernieuwd en snapshot opgeslagen');
      await fetchStats();
    } catch (error) {
      console.error('[useIncassoShared] Error refreshing:', error);
      toast.error('Fout bij vernieuwen van incasso data');
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, fetchStats]);

  // ─── Stats click handlers ──────────────────────────────────────────────────
  const handlePaymentPlanStatsClick = useCallback(() => {
    setPhaseFilter(IncassoPhase.MINNELIJKE_REGELING);
  }, []);

  const handleLegalProcedureStatsClick = useCallback(() => {
    setPhaseFilter(IncassoPhase.JURIDISCHE_PROCEDURE_LOPEND);
  }, []);

  const handleSnoozedStatsClick = useCallback(() => {
    setIncludeSnoozed(true);
  }, []);

  const handleIncassoViaDerdeStatsClick = useCallback(() => {
    setPhaseFilter(IncassoPhase.INCASSO_VIA_DERDE);
  }, []);

  return {
    // Filter state
    searchText,
    setSearchText,
    beoordelingFilter,
    setBeoordelingFilter,
    buildingFilters,
    setBuildingFilters,
    companyFilters,
    setCompanyFilters,
    phaseFilter,
    setPhaseFilter,
    debtBucket,
    setDebtBucket,
    includeSnoozed,
    setIncludeSnoozed,
    noIncassoActions,
    setNoIncassoActions,
    isRestoringFromURL,

    // Computed filters for API calls
    apiFilters,

    // Stats
    stats,
    isLoadingStats,
    fetchStats,
    totalUnpaidRent,

    // Sync state
    hasSynced,

    // Kanban-specific
    hiddenKanbanColumns,
    kanbanFontSize,
    handleToggleColumnVisibility,
    handleIncreaseFontSize,
    handleDecreaseFontSize,

    // FilterBar
    filterBarSections,
    filterBarChips,
    handleFiltersFromURL,

    // Actions
    handleUpdateTicket,
    handlePhaseChange,
    handleSnooze,
    handleUnsnooze,
    handleBeoordelingChange,

    // Modal state
    snoozeModal,
    setSnoozeModal,
    phaseTransitionPending,
    setPhaseTransitionPending,
    actionModal,
    setActionModal,
    legalModal,
    setLegalModal,
    paymentPlanModal,
    setPaymentPlanModal,

    // Stats click handlers
    handlePaymentPlanStatsClick,
    handleLegalProcedureStatsClick,
    handleSnoozedStatsClick,
    handleIncassoViaDerdeStatsClick,

    // Refresh
    isRefreshing,
    handleRefresh,

    // Export
    isExporting,
    handleExportToExcel,

    // View mode
    viewMode,
  };
};

export type IncassoSharedState = ReturnType<typeof useIncassoShared>;
